import { useEffect } from 'react';
import { Callback } from './app/Callback';
import { Home } from './app/Home';
import { Join } from './app/Join';
import type { ServerTab } from './app/ServerPage';
import { Shell } from './app/Shell';
import { SignIn } from './app/SignIn';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { Landing } from './landing/Landing';
import { navigate, safeReturnTo, usePath } from './router';

/** The app: the front door at `/`, the sign-in callback, and the signed-in pages under `/app`. */
export function App({ path }: { path?: string }) {
  return (
    <AuthProvider>
      <Routes path={path} />
    </AuthProvider>
  );
}

function Routes({ path: fixed }: { path?: string }) {
  const live = usePath();
  const path = fixed ?? live;
  if (path === '/auth/callback') return <Callback />;
  if (path === '/signin') return <SignIn returnTo={safeReturnTo(new URLSearchParams(globalThis.location?.search ?? '').get('returnTo'))} />;
  // Signed in or not: the page itself offers sign-in, and comes back here after it.
  const join = /^\/join\/([^/]+)$/.exec(path);
  if (join) return <Join code={decodeURIComponent(join[1]!)} />;
  const server = /^\/app\/servers\/([^/]+)(?:\/(friends|backups|usage|settings))?$/.exec(path);
  if (server) {
    const tab = (server[2] ?? 'overview') as ServerTab;
    return <SignedIn path={path}><Home serverId={decodeURIComponent(server[1]!)} tab={tab} /></SignedIn>;
  }
  if (path === '/app/admin') return <SignedIn path={path}><Home page="admin" /></SignedIn>;
  if (path === '/app/new') return <SignedIn path={path}><Home page="new" /></SignedIn>;
  if (path === '/app' || path.startsWith('/app/')) return <SignedIn path={path}><Home /></SignedIn>;
  if (path !== '/') return <NotFound />;
  return <Landing />;
}

/** Pages for signed-in people: anyone else goes to Hearth's sign-in page, and back here after. */
function SignedIn({ path, children }: { path: string; children: React.ReactNode }) {
  const { status } = useAuth();
  useEffect(() => {
    if (status === 'signed-out') navigate(`/signin?returnTo=${encodeURIComponent(path)}`, { replace: true });
  }, [status, path]);
  if (status === 'signed-in') return children;
  return (
    <Shell>
      <p className="quiet" aria-live="polite">{status === 'unavailable' ? "Hearth can't be reached right now. Please try again in a moment." : 'Loading…'}</p>
    </Shell>
  );
}

function NotFound() {
  useEffect(() => navigate('/', { replace: true }), []);
  return null;
}
